import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

/**
 * Story 67.4 — Admin page for inbound voice DID routing configuration.
 * Route: /admin/voice-routes
 */

interface InboundRoute {
  id: string;
  didPhoneNumber: string;
  scriptId: string;
  fallbackUserId: string | null;
  isActive: boolean;
  createdAt: string;
}

interface VoiceScript {
  id: string;
  name: string;
  version: number;
}

export function VoiceRoutesPage() {
  const { t } = useTranslation();
  const [routes, setRoutes] = useState<InboundRoute[]>([]);
  const [scripts, setScripts] = useState<VoiceScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    Promise.all([
      apiClient.get<InboundRoute[]>('/voice/inbound-routes'),
      apiClient.get<VoiceScript[]>('/voice/scripts'),
    ])
      .then(([r, s]) => { setRoutes(r.data); setScripts(s.data); })
      .catch((err) => setError(isAxiosError(err) ? err.message : String(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  function handleDelete(id: string) {
    if (!window.confirm(t('voice.routes.confirmDelete'))) return;
    apiClient.delete(`/voice/inbound-routes/${id}`).then(load);
  }

  if (loading) return <div className="text-muted-foreground text-sm">{t('app.loading', 'Loading...')}</div>;
  if (error) return <div className="text-destructive text-sm">{error}</div>;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('voice.routes.title')}</h1>

      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setShowNew(true); setEditId(null); }}>
          <Plus className="h-4 w-4 mr-1" />
          {t('voice.routes.new')}
        </Button>
      </div>

      {(showNew || editId) && (
        <RouteForm
          routeId={editId}
          scripts={scripts}
          onSaved={() => { setShowNew(false); setEditId(null); load(); }}
          onCancel={() => { setShowNew(false); setEditId(null); }}
        />
      )}

      {routes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('voice.routes.empty')}</p>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-4 py-2">{t('voice.routes.did')}</th>
                <th className="px-4 py-2">{t('voice.routes.script')}</th>
                <th className="px-4 py-2">{t('voice.routes.active')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {routes.map((route) => {
                const script = scripts.find((s) => s.id === route.scriptId);
                return (
                  <tr key={route.id} className="border-t hover:bg-muted/30">
                    <td className="px-4 py-2 font-mono">{route.didPhoneNumber}</td>
                    <td className="px-4 py-2">{script ? `${script.name} v${script.version}` : route.scriptId}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${route.isActive ? 'bg-emerald-900/40 text-emerald-200' : 'bg-muted text-muted-foreground'}`}>
                        {route.isActive ? t('voice.routes.active') : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2 flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => { setEditId(route.id); setShowNew(false); }}>
                        {t('voice.scripts.edit')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(route.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function RouteForm({
  routeId,
  scripts,
  onSaved,
  onCancel,
}: {
  routeId: string | null;
  scripts: VoiceScript[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [did, setDid] = useState('');
  const [scriptId, setScriptId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (routeId) {
      apiClient.get<InboundRoute>(`/voice/inbound-routes/${routeId}`).then((r) => {
        setDid(r.data.didPhoneNumber);
        setScriptId(r.data.scriptId);
        setIsActive(r.data.isActive);
      });
    }
  }, [routeId]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    const req = routeId
      ? apiClient.put(`/voice/inbound-routes/${routeId}`, { scriptId, isActive })
      : apiClient.post('/voice/inbound-routes', { didPhoneNumber: did, scriptId, isActive });
    req
      .then(onSaved)
      .catch((err) => setError(isAxiosError(err) ? err.message : String(err)))
      .finally(() => setSaving(false));
  }

  return (
    <Card className="p-4 space-y-3">
      <h2 className="font-medium">{routeId ? t('voice.scripts.edit') : t('voice.routes.new')}</h2>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-3">
        {!routeId && (
          <div>
            <label className="block text-sm font-medium mb-1">{t('voice.routes.did')}</label>
            <input
              className="w-full border rounded px-3 py-1.5 text-sm font-mono"
              placeholder="+15555550100"
              value={did}
              onChange={(e) => setDid(e.target.value)}
              required
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">{t('voice.routes.script')}</label>
          <select className="w-full border rounded px-3 py-1.5 text-sm" value={scriptId} onChange={(e) => setScriptId(e.target.value)} required>
            <option value="">—</option>
            {scripts.map((s) => <option key={s.id} value={s.id}>{s.name} v{s.version}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          {t('voice.routes.active')}
        </label>
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={saving}>{t('voice.routes.save')}</Button>
          <Button type="button" size="sm" variant="outline" onClick={onCancel}>{t('app.cancel', 'Cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
